// commons/apiClient.js
import { getCurrentUser, refreshSessionIfTenantPending } from "./auth";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const GRAPHQL_URI = process.env.REACT_APP_GRAPHQL_URI;
const PRESIGNED_URI = process.env.REACT_APP_API_PRESIGNED_URL_ENDPOINT;
const CHANNEL = process.env.REACT_APP_CHANNEL;

/** Bearer para API Gateway: IdToken (custom:tenant_id vía pretokengen). Ver postman/cognito_id_token. */
async function getAccessToken() {
  await refreshSessionIfTenantPending().catch(() => {});

  return new Promise((resolve, reject) => {
    const user = getCurrentUser();
    if (!user) return reject("No current user");

    user.getSession((err, session) => {
      if (err) return reject(err);
      if (!session.isValid()) return reject("Session expired");
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

export { getAccessToken };

function buildGraphqlUrl() {
  if (!API_BASE_URL || !GRAPHQL_URI) {
    throw new Error(
      "Configuración API incompleta: REACT_APP_API_BASE_URL y REACT_APP_GRAPHQL_URI son obligatorios"
    );
  }
  return `${API_BASE_URL}${GRAPHQL_URI}`;
}

export async function graphqlRequest(query, variables = {}) {
  const token = await getAccessToken();
  const url = buildGraphqlUrl();

  let res;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ query, variables })
    });
  } catch (networkError) {
    console.error("GraphQL network error:", { url, networkError });
    throw new Error(
      "No se pudo conectar con la API. Verificá tu conexión y que REACT_APP_API_BASE_URL esté configurada."
    );
  }

  let data;
  try {
    data = await res.json();
  } catch (parseError) {
    throw new Error(`Respuesta inválida del servidor (HTTP ${res.status})`);
  }

  if (!res.ok) {
    const message = data?.message || data?.error || `HTTP ${res.status}`;
    throw new Error(message);
  }

  if (data.errors) throw new Error(JSON.stringify(data.errors));
  return data.data;
}

export async function graphqlRequestWithToken(token, query, variables = {}) {
  const url = buildGraphqlUrl();
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ query, variables }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data?.message || `HTTP ${res.status}`);
  }
  if (data.errors) throw new Error(JSON.stringify(data.errors));
  return { data: data.data, errors: null, status: res.status };
}

export async function graphqlRequestRaw(query, variables = {}, tokenOverride = null) {
  const token = tokenOverride || (await getAccessToken());
  const url = buildGraphqlUrl();
  const started = performance.now();
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ query, variables }),
  });
  const elapsed = Math.round(performance.now() - started);
  let body;
  try {
    body = await res.json();
  } catch {
    body = { error: `Invalid response (HTTP ${res.status})` };
  }
  return { status: res.status, elapsed, body, url, tokenUsed: tokenOverride ? "pat" : "session" };
}

export function getGraphqlUrl() {
  return buildGraphqlUrl();
}

// === Presigned URL ===

export class DuplicateUploadError extends Error {
  constructor(payload) {
    super(payload.message || 'Archivo duplicado');
    this.name = 'DuplicateUploadError';
    this.code = payload.error;
    this.existingDocumentId = payload.existing_document_id;
    this.existingFilename = payload.existing_filename;
    this.processedAt = payload.processed_at;
    this.data = payload;
  }
}

export function isDuplicateUploadError(err) {
  return (
    err instanceof DuplicateUploadError ||
    err?.name === 'DuplicateUploadError' ||
    err?.code === 'DUPLICATE_CONTENT' ||
    err?.code === 'UPLOAD_IN_PROGRESS'
  );
}

export async function getPresignedUrl(payload) {
  try {
 
    // Validación de entrada
    if (!payload?.key || !payload?.presigned_type || !payload?.file_name) {
      throw new Error("Parámetros requeridos faltantes: key, presigned_type y file_name son obligatorios");
    }

    // Obtener token con manejo de errores
    let token;
    try {
      token = await getAccessToken();
      if (!token) {
        throw new Error("No se pudo obtener el token de acceso");
      }
    } catch (error) {
      console.error("Error obteniendo token:", error);
      throw new Error("Error de autenticación: No se pudo obtener el token de acceso");
    }

    const request_payload = {
      key: payload.key,
      presigned_type: payload.presigned_type,
      channel: CHANNEL,
      file_name: payload.file_name,
    };
    if (payload.content_md5) {
      request_payload.content_md5 = payload.content_md5;
    }
    if (payload.file_size != null) {
      request_payload.file_size = payload.file_size;
    }

    // Realizar petición HTTP
    let res;
    try {
      res = await fetch(`${API_BASE_URL}${PRESIGNED_URI}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(request_payload)
      });
    } catch (error) {
      console.error("Error en la petición HTTP:", error);
      throw new Error("Error de conexión: No se pudo conectar con el servidor");
    }

    // Manejo de respuestas HTTP
    if (!res.ok) {
      let errorMessage = "Error al obtener presigned URL";
      let errorData = null;

      try {
        errorData = await res.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (parseError) {
        console.warn("No se pudo parsear la respuesta de error:", parseError);
      }

      switch (res.status) {
        case 400:
          throw new Error(`Solicitud inválida: ${errorMessage}`);
        case 401:
          throw new Error("No autorizado: Token inválido o expirado");
        case 403:
          throw new Error(
            errorMessage.startsWith("Acceso denegado")
              ? errorMessage
              : `Acceso denegado: ${errorMessage}`
          );
        case 404:
          throw new Error("Recurso no encontrado");
        case 409:
          throw new DuplicateUploadError(
            errorData || { message: errorMessage, error: "DUPLICATE_CONTENT" }
          );
        case 429:
          throw new Error("Demasiadas solicitudes: Intente nuevamente más tarde");
        case 500:
        case 502:
        case 503:
        case 504:
          throw new Error("Error del servidor: Intente nuevamente más tarde");
        default:
          throw new Error(`Error HTTP ${res.status}: ${errorMessage}`);
      }
    }

    // Parsear y validar respuesta
    let responseData;
    try {
      responseData = await res.json();

      // Validación básica de la estructura de respuesta
      if (!responseData || typeof responseData !== 'object') {
        throw new Error("Respuesta inválida del servidor");
      }

      // Validar que contenga los campos esperados
      if (!responseData.url) {
        throw new Error("Respuesta incompleta: URL faltante");
      }

      return responseData; // {url:..., fields:...} o {url:...}

    } catch (parseError) {
      console.error("Error parseando respuesta JSON:", parseError);
      throw new Error("Error procesando respuesta del servidor");
    }

  } catch (error) {
    if (!isDuplicateUploadError(error)) {
      console.error('Error en getPresignedUrl:', {
        message: error.message,
        payload,
        timestamp: new Date().toISOString(),
      });
    }
    throw error;
  }
}

// === SPEC-13 Upload API ===

const ALLOWED_UPLOAD_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/zip',
];
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

async function uploadApiFetch(path, { method = 'GET', body } = {}) {
  const token = await getAccessToken();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try {
    data = await res.json();
  } catch {
    data = {};
  }
  if (!res.ok) {
    if (res.status === 409) {
      throw new DuplicateUploadError(data || { message: 'Archivo duplicado', error: data.error });
    }
    const err = new Error(data.message || data.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export async function uploadPresign({ filename, content_type, size_bytes, content_md5 }) {
  return uploadApiFetch('/v1/uploads/presign', {
    method: 'POST',
    body: { filename, content_type, size_bytes, content_md5 },
  });
}

export async function uploadComplete(uploadId) {
  return uploadApiFetch(`/v1/uploads/${uploadId}/complete`, { method: 'POST' });
}

export async function uploadStatus(uploadId) {
  return uploadApiFetch(`/v1/uploads/${uploadId}/status`);
}

export async function uploadTouch(uploadId) {
  return uploadApiFetch(`/v1/uploads/${uploadId}/touch`, { method: 'POST' });
}

const LIST_RECENT_UPLOADS_QUERY = `
  query ListRecentUploads($limit: Int) {
    listRecentUploads(limit: $limit) {
      queueDate
      items {
        uploadId
        documentId
        filename
        sizeBytes
        status
        docType
        duplicateOf
        comprobanteLabel
        arcaValidation {
          status
          severity
          checked_at
          observations
          errors
        }
        errorMessage
        createdAt
        updatedAt
      }
    }
  }
`;

export async function listRecentUploads(limit = 5) {
  const data = await graphqlRequest(LIST_RECENT_UPLOADS_QUERY, { limit });
  return data?.listRecentUploads || { items: [], queueDate: null };
}

export async function getTenantEmailAlias() {
  return uploadApiFetch('/v1/tenant/email-alias');
}

// === SPEC-12 Document Ficha API ===

export async function fetchDocumentFicha(docId) {
  if (process.env.REACT_APP_DOCUMENT_V1_ENABLED !== 'true') return null;
  return uploadApiFetch(`/v1/documents/${docId}`);
}

export async function patchDocumentField(docId, field, value) {
  return uploadApiFetch(`/v1/documents/${docId}/fields`, {
    method: 'PATCH',
    body: { field, value },
  });
}

export async function constatarDocumentArca(docId) {
  if (process.env.REACT_APP_DOCUMENT_V1_ENABLED !== 'true') {
    const data = await graphqlRequest(`
      mutation ValidateArcaDocument($documentId: ID!, $force: Boolean) {
        validateArcaDocument(document_id: $documentId, force: $force) {
          document_id
          arca_validation {
            status
            severity
            result
            checked_at
            environment
            observations
            errors
            process_date
            audit_id
            retryable
            attempts
            comparison {
              key
              label
              extracted_value
              arca_value
              match
            }
          }
          audit {
            attempt_id
            trigger
            environment
            duration_ms
            created_at
            comparison {
              key
              label
              extracted_value
              arca_value
              match
            }
          }
        }
      }
    `, { documentId: docId, force: true });
    return data?.validateArcaDocument;
  }
  return uploadApiFetch(`/v1/documents/${docId}/arca/constatar`, { method: 'POST' });
}

export async function fetchArcaValidationAudit(docId, limit = 1) {
  const data = await graphqlRequest(`
    query GetArcaValidationAudit($documentId: ID!, $limit: Int) {
      getArcaValidationAudit(document_id: $documentId, limit: $limit) {
        attempt_id
        trigger
        environment
        duration_ms
        created_at
        mapped_status
        comparison {
          key
          label
          extracted_value
          arca_value
          match
        }
      }
    }
  `, { documentId: docId, limit });
  return data?.getArcaValidationAudit || [];
}

export async function classifyDocument(docId, targetDocType) {
  return uploadApiFetch(`/v1/documents/${docId}/classify`, {
    method: 'POST',
    body: { target_doc_type: targetDocType },
  });
}

export function mapPresignError(status, body = {}) {
  if (status === 415) return 'Tipo de archivo no soportado';
  if (status === 413) return 'El archivo supera el máximo de 25 MB';
  if (status === 429) return 'Alcanzaste el límite de comprobantes de tu plan este mes';
  if (status === 409) {
    if (body.error === 'UPLOAD_IN_PROGRESS') {
      return 'Este archivo idéntico ya se está procesando';
    }
    return 'Este archivo ya fue procesado anteriormente';
  }
  return 'No se pudo iniciar la carga';
}

export function validateUploadFile(file) {
  if (!ALLOWED_UPLOAD_TYPES.includes(file.type)) {
    return `Tipo no soportado (${file.type || 'desconocido'})`;
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return `Supera el máximo de 25 MB (${(file.size / 1e6).toFixed(1)} MB)`;
  }
  return null;
}

// === SPEC-25 PAT admin REST ===

async function patAdminFetch(path, { method = "GET", body } = {}) {
  if (!API_BASE_URL) {
    throw new Error("REACT_APP_API_BASE_URL no configurada");
  }
  const token = await getAccessToken();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try {
    data = await res.json();
  } catch {
    data = {};
  }
  if (!res.ok) {
    const message =
      (typeof data.error === "object" ? data.error.message : data.error) ||
      data.message ||
      `HTTP ${res.status}`;
    throw new Error(message);
  }
  return data;
}

export async function listPatApiTokens() {
  const data = await patAdminFetch("/admin/api-tokens");
  return data.items || [];
}

export async function createPatApiToken({ label, scopes }) {
  return patAdminFetch("/admin/api-tokens", {
    method: "POST",
    body: { label, scopes },
  });
}

export async function rotatePatApiToken(patId) {
  return patAdminFetch(`/admin/api-tokens/${encodeURIComponent(patId)}/rotate`, {
    method: "POST",
  });
}

export async function revokePatApiToken(patId) {
  return patAdminFetch(`/admin/api-tokens/${encodeURIComponent(patId)}`, {
    method: "DELETE",
  });
}

export async function exchangePatCredentials(clientId, accessKey) {
  if (!API_BASE_URL) {
    throw new Error("REACT_APP_API_BASE_URL no configurada");
  }
  const basic = btoa(`${clientId}:${accessKey}`);
  const res = await fetch(`${API_BASE_URL}/auth`, {
    method: "POST",
    headers: {
      Authorization: `Basic ${basic}`,
    },
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data?.error?.message || `HTTP ${res.status}`);
  }
  return data;
}